import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import { LevelActionType } from '~/bootcamp/types/bootcamp.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectBootcampPageEncyclopediaArticle, setBootcampIsCollapsed } from '~/bootcamp/slices/bootcamp-page.slice'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import useTrackHelpToggle from '~/bootcamp/hooks/useTrackHelpToggle'
import { TopicId, getPathGradientColors } from '~/dashboard/utils/learningPath.utils'
import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'
import { GRADE_QUERY_PARAM } from '~/bootcamp/constants/bootcamp.const'
import BootcampVideoProgressReward from '~/components/BootcampVideoProgressReward'
import { selectProgressPercentage, selectVideoLength } from '~/bootcamp/slices/bootcamp-video.slice'
import getVideoXP from '~/bootcamp/utils/getVideoXP.utils'

import { selectVccHasWhyLevel } from '~/bootcamp/slices/vcc-data.slice'

import { useGetIsNorwegianCurriculum } from '~/curriculum/hooks/useGetCurriculumCountryCode'

import HelpToggleButton from '../BootcampPageHelp/HelpToggleButton'
import BootcampPageHelp from '../BootcampPageHelp/BootcampPageHelp'

import QuizPageHeader from './QuizPageHeader'
import PageHeader from './PageHeader'

const VideoCourseIcon = dynamic(() => import('~/bootcamp/components/VideoCourseIcon'), { ssr: false })

interface Props {
  title: string
  currentPath: string
  path: string
  type: LevelActionType
  isCompleted: boolean
  children: React.ReactElement
  level: number
  isCollapsed: boolean
  icon: LevelActionType
}

const CoursePageCollapse = ({
  title,
  isCollapsed,
  currentPath,
  path,
  type,
  isCompleted,
  level,
  icon,
  children,
}: Props): React.ReactElement => {
  const [isHelpVisible, toggleHelpVisibility] = useState(false)

  const dispatch = useAppDispatch()
  const isExpanded = path === currentPath
  const isQuizPage = type === LevelActionType.Quiz

  const containerRef = useRef<HTMLDivElement>(null)
  const pageContentRef = useRef<HTMLDivElement>(null)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.MD })
  const isVideo = type === LevelActionType.Definition || type === LevelActionType.How || type === LevelActionType.Why
  const hasWhyLevel = useAppSelector(selectVccHasWhyLevel)
  const isFirstPage = type === LevelActionType.Why || (!hasWhyLevel && level === 1)
  const videoProgressPercentage = useAppSelector(selectProgressPercentage)
  const videoLength = useAppSelector(selectVideoLength)
  const encyclopediaArticle = useAppSelector(selectBootcampPageEncyclopediaArticle)

  const isGradePage = useIsBootcampGradePage()
  const isNorwegianCurriculum = useGetIsNorwegianCurriculum()
  const hideUsefullWords = isGradePage && isNorwegianCurriculum

  const videoXP = getVideoXP(videoLength)
  const trackHelpToggle = useTrackHelpToggle()
  const {
    query: { showGrade, subtopic },
  } = useRouter()

  const color = getPathGradientColors(subtopic as TopicId)

  const scrollPageIntoView = useCallback((): void => {
    containerRef?.current?.scrollIntoView({
      block: showGrade || isMobile ? 'start' : 'center',
      inline: 'nearest',
      behavior: 'smooth',
    })
  }, [isMobile, showGrade])

  useEffect(() => {
    if (isExpanded && !isCollapsed) {
      if (isFirstPage) {
        const timer = setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        }, 10)

        return () => clearTimeout(timer)
      } else {
        const timer = setTimeout(() => {
          scrollPageIntoView()
          pageContentRef.current?.classList.remove('overflow-hidden')
        }, 400)
        return () => clearTimeout(timer)
      }
    } else {
      pageContentRef.current?.classList.add('overflow-hidden')
    }
  }, [isExpanded, isFirstPage, type, isCollapsed, scrollPageIntoView])

  const onCollapseHeaderClick = (): void => {
    dispatch(setBootcampIsCollapsed(isExpanded && !isCollapsed))
  }

  const toggleHelpPopup = (showHelp: boolean): void => {
    trackHelpToggle(showHelp)
    toggleHelpVisibility(showHelp)
  }

  const handleHelpIconClick = (): void => {
    trackHelpToggle(true)
    toggleHelpPopup(true)
    scrollPageIntoView()
  }

  const bootcampHelp = (
    <Fragment>
      <BootcampPageHelp
        className="absolute top-1/2 -translate-y-1/2 md:translate-y-0 md:top-20 z-40 left-1/2 transform -translate-x-1/2"
        onClose={() => toggleHelpPopup(false)}
      />
      <div className="fixed inset-0 bg-white bg-opacity-70 z-30" onClick={() => toggleHelpPopup(false)} />
    </Fragment>
  )

  const isLargeIcon = isExpanded && !isCollapsed && !isCompleted && !isMobile
  return (
    <div className={`relative ${isLargeIcon && 'mt-6'}`}>
      {!isQuizPage && (
        <VideoCourseIcon
          className={isCompleted && !isQuizPage ? `relative z-10 -left-2 h-9` : `absolute z-10 mt-7 ml-2`}
          color={color}
          iconType={icon}
          size={isLargeIcon ? 'large' : 'small'}
        />
      )}
      {isExpanded && !isCollapsed && !isQuizPage && (
        <>
          {!hideUsefullWords && (
            <HelpToggleButton
              className={`fixed md:absolute bottom-24 right-4 md:bottom-auto md:-right-11 md:top-4 z-20 ${
                isHelpVisible ? 'hidden' : 'block'
              }`}
              onClick={handleHelpIconClick}
            />
          )}
          {!encyclopediaArticle && isVideo && isDesktop && (
            <div className="absolute flex bottom-auto w-50 -right-52 top-16">
              <BootcampVideoProgressReward percent={videoProgressPercentage} textColor="text-white" xp={videoXP} />
            </div>
          )}
        </>
      )}

      {isHelpVisible && bootcampHelp}

      <section className={`pt-4 mb-2 overflow-hidden rounded relative ${isCompleted && '-mt-9'}`} ref={containerRef}>
        <div className={` rounded  ${isQuizPage ? 'border-black' : 'border-white'}`}>
          {isQuizPage ? (
            <QuizPageHeader
              isCollapsed={isCollapsed}
              isCompleted={isCompleted}
              isExpanded={isExpanded}
              onClick={onCollapseHeaderClick}
              path={path}
              title={title}
              type={type}
            />
          ) : (
            <PageHeader
              isCollapsed={isCollapsed}
              isCompleted={isCompleted}
              isExpanded={isExpanded}
              onClick={onCollapseHeaderClick}
              path={path + (isGradePage ? `?${GRADE_QUERY_PARAM}=true` : '')}
              title={title}
              type={type}
            />
          )}

          <div
            className="overflow-hidden"
            ref={pageContentRef}
            style={{
              maxHeight: isExpanded && !isCollapsed ? '9999rem' : 0,
              transition: `max-height 400ms cubic-bezier(0.4, 0, 1, 1)`,
            }}
          >
            {isExpanded && children}
          </div>
        </div>
      </section>
    </div>
  )
}

export default CoursePageCollapse
